/**
 * Runtime configuration validation (fail-fast).
 *
 * Enforces the rules in contracts/environment-config.md at startup (T011, FR-025, SC-003).
 * On any violation, config/index.ts throws ConfigValidationError listing every offending key,
 * which main.tsx renders as ConfigErrorPage. The app MUST NOT boot half-configured.
 */

import type { RuntimeConfig, ValidationResult } from './types';

/** True if the string parses as an absolute URL. */
export function isValidUrl(url: string): boolean {
  if (!url) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/** True if the URL points at localhost / a loopback address. */
export function isLocalhostUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '0.0.0.0';
  } catch {
    return false;
  }
}

/**
 * Validate a fully-loaded RuntimeConfig.
 *
 * @returns { valid, errors } — errors lists every offending key/reason.
 */
export function validateConfig(config: RuntimeConfig): ValidationResult {
  const errors: string[] = [];

  // environment
  if (!['local', 'dev', 'prod'].includes(config.environment)) {
    errors.push(
      `environment: "${config.environment}" is invalid. Must be one of: local, dev, prod.`
    );
  }

  // authMode
  if (!['mock', 'oidc'].includes(config.authMode)) {
    errors.push(`authMode: "${config.authMode}" is invalid. Must be one of: mock, oidc.`);
  }

  // mock only allowed in local
  if (config.authMode === 'mock' && config.environment !== 'local') {
    errors.push(
      `authMode: "mock" is only permitted when environment is "local" (got "${config.environment}"). ` +
        `Use authMode "oidc" for dev/prod.`
    );
  }

  // devProxy (local-dev CORS proxy) only allowed in local — guarantees it can never activate in a
  // deployed environment, upholding the single-image guarantee (feature 002, FR-002/FR-003).
  if (config.devProxy === true && config.environment !== 'local') {
    errors.push(
      `devProxy: is only permitted when environment is "local" (got "${config.environment}").`
    );
  }

  // apiBaseUrls: at least one entry, each an absolute URL, non-localhost must be https
  const keys = Object.keys(config.apiBaseUrls);
  if (keys.length === 0) {
    errors.push('apiBaseUrls: at least one backend target is required (empty map).');
  }
  for (const [key, url] of Object.entries(config.apiBaseUrls)) {
    if (!isValidUrl(url)) {
      errors.push(`apiBaseUrls["${key}"]: "${url}" is not an absolute URL.`);
      continue;
    }
    if (!isLocalhostUrl(url) && !url.startsWith('https://')) {
      errors.push(`apiBaseUrls["${key}"]: "${url}" must use https:// (only localhost may use http://).`);
    }
  }

  // defaultApiKey must reference a real key
  if (!config.defaultApiKey) {
    errors.push('defaultApiKey: is required.');
  } else if (!keys.includes(config.defaultApiKey)) {
    errors.push(
      `defaultApiKey: "${config.defaultApiKey}" is not a key in apiBaseUrls (available: ${keys.join(', ') || 'none'}).`
    );
  }

  // Monitored services (liveness dashboard). Optional; when present, fail-fast on bad shapes.
  if (config.services !== undefined) {
    const services = config.services;
    if (!Array.isArray(services) || services.length === 0) {
      errors.push('services: when present, must be a non-empty array of monitored services.');
    } else {
      const seenIds = new Set<string>();
      services.forEach((service, i) => {
        if (!service || typeof service !== 'object') {
          errors.push(`services[${i}]: must be an object with id, displayName, and target.`);
          return;
        }
        const id = typeof service.id === 'string' ? service.id.trim() : '';
        if (!id) {
          errors.push(`services[${i}].id: is required (non-empty string).`);
        } else if (seenIds.has(id)) {
          errors.push(`services[${i}].id: "${id}" is duplicated; ids must be unique.`);
        } else {
          seenIds.add(id);
        }

        if (typeof service.displayName !== 'string' || service.displayName.trim() === '') {
          errors.push(`services[${i}].displayName: is required (non-empty string).`);
        }

        const target = typeof service.target === 'string' ? service.target.trim() : '';
        if (!target) {
          errors.push(`services[${i}].target: is required (non-empty string).`);
        } else if (!keys.includes(target)) {
          errors.push(
            `services[${i}].target: "${target}" is not a key in apiBaseUrls (available: ${keys.join(', ') || 'none'}).`
          );
        }

        if (service.healthPath !== undefined) {
          if (
            typeof service.healthPath !== 'string' ||
            service.healthPath.trim() === '' ||
            !service.healthPath.startsWith('/')
          ) {
            errors.push(
              `services[${i}].healthPath: when present, must be a non-empty string beginning with "/".`
            );
          }
        }
      });
    }
  }

  // Auto-refresh interval (liveness dashboard). Optional; when present, positive integer.
  if (config.livenessRefreshIntervalMs !== undefined) {
    const ms = config.livenessRefreshIntervalMs;
    if (typeof ms !== 'number' || !Number.isInteger(ms) || ms <= 0) {
      errors.push(
        `livenessRefreshIntervalMs: "${ms}" is invalid; when present, must be a positive integer (milliseconds).`
      );
    }
  }

  // API Route Explorer target (feature 003). Optional; when present, must name a real backend.
  if (config.apiExplorerTarget !== undefined) {
    const t = config.apiExplorerTarget;
    if (typeof t !== 'string' || t.trim() === '') {
      errors.push('apiExplorerTarget: when present, must be a non-empty string.');
    } else if (!keys.includes(t)) {
      errors.push(
        `apiExplorerTarget: "${t}" is not a key in apiBaseUrls (available: ${keys.join(', ') || 'none'}).`
      );
    }
  }

  // OIDC requirements
  if (config.authMode === 'oidc') {
    const oidc = config.oidc;
    if (!oidc) {
      errors.push('oidc: configuration block is required when authMode is "oidc".');
    } else {
      if (!oidc.authority) {
        errors.push('oidc.authority: is required when authMode is "oidc".');
      } else if (!isValidUrl(oidc.authority)) {
        errors.push(`oidc.authority: "${oidc.authority}" is not a valid URL.`);
      } else if (config.environment === 'prod' && !oidc.authority.startsWith('https://')) {
        errors.push('oidc.authority: must use https:// in prod.');
      }

      if (!oidc.clientId) {
        errors.push('oidc.clientId: is required when authMode is "oidc".');
      }

      if (!oidc.scope || !oidc.scope.split(/\s+/).includes('openid')) {
        errors.push('oidc.scope: must contain "openid" for OIDC compliance.');
      }
    }
  }

  return { valid: errors.length === 0, errors };
}
